import {
  deriveBinaryBucket,
  getConfidenceDisplayMode,
  type ConfidenceDisplayMode,
} from '../../utils/confidenceDisplay';
import styles from './ConfidenceBadge.module.css';

export type ConfidenceBucket = 'high' | 'medium' | 'low';

interface ConfidenceBadgeProps {
  bucket: ConfidenceBucket | null | undefined;
  /** Prefix the label with "AI · " — used on dense list rows. */
  prefix?: boolean;
  /**
   * Override the display mode. When omitted, the global feature flag
   * decides (env var VITE_CONFIDENCE_BINARY_BADGE or localStorage).
   *
   * `3bucket` — High / Medium / Low (legacy, current default).
   * `binary`  — High / Low; Medium collapses to Low.
   */
  mode?: ConfidenceDisplayMode;
}

const THREE_BUCKET_LABELS: Record<ConfidenceBucket, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({
  bucket,
  prefix = false,
  mode,
}) => {
  if (bucket !== 'high' && bucket !== 'medium' && bucket !== 'low') {
    return null;
  }
  const effectiveMode: ConfidenceDisplayMode = mode ?? getConfidenceDisplayMode();
  let renderBucket: ConfidenceBucket = bucket;
  let label: string = THREE_BUCKET_LABELS[bucket];
  if (effectiveMode === 'binary') {
    const binary = deriveBinaryBucket(bucket);
    if (binary === null) return null;
    renderBucket = binary;
    label = binary === 'high' ? 'High' : 'Low';
  }
  return (
    <span
      className={`${styles.badge} ${styles[renderBucket]}`}
      title="Deterministic AI extraction confidence"
    >
      {prefix ? `AI · ${label}` : label}
    </span>
  );
};

export default ConfidenceBadge;
